import { getApiUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import { Globe, Home, Eye, CheckCircle, ChevronDown, Loader2 } from "lucide-react";
import axios from "axios";

const API = getApiUrl();

const STEP_CONFIG = [
  { icon: Globe, color: "bg-blue-500", light: "bg-blue-50 border-blue-200", text: "text-blue-900", bar: "bg-blue-500" },
  { icon: Home, color: "bg-teal-500", light: "bg-teal-50 border-teal-200", text: "text-teal-900", bar: "bg-teal-500" },
  { icon: Eye, color: "bg-amber-500", light: "bg-amber-50 border-amber-200", text: "text-amber-900", bar: "bg-amber-500" },
  { icon: CheckCircle, color: "bg-emerald-500", light: "bg-emerald-50 border-emerald-200", text: "text-emerald-900", bar: "bg-emerald-500" },
];

function FunnelStep({ step, config, maxCount, isLast }) {
  const widthPct = maxCount > 0 ? Math.max((step.count / maxCount) * 100, 5) : 5;
  const Icon = config.icon;

  return (
    <div className="space-y-1">
      <div className={`border rounded-lg p-4 ${config.light}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${config.color} rounded-lg`}>
              <Icon className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className={`text-sm font-semibold ${config.text}`}>{step.step}</p>
              <p className="text-xs text-muted-foreground">{step.label}</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-bold font-serif ${config.text}`}>{step.count.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">{step.pct}% of previous</p>
          </div>
        </div>
        {/* Width bar */}
        <div className="h-2.5 bg-white/60 rounded-full overflow-hidden">
          <div
            className={`h-full ${config.bar} rounded-full transition-all duration-700`}
            style={{ width: `${widthPct}%` }}
          />
        </div>
      </div>

      {!isLast && (
        <div className="flex items-center justify-center py-1">
          <div className="flex items-center gap-2">
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
            {step.drop_off > 0 && (
              <span className="text-xs text-rose-600 font-medium">
                {step.drop_off}% drop-off
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AnalyticsFunnel({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/analytics/funnel?period=${period}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  const steps = data?.steps || [];
  const maxCount = steps[0]?.count || 1;
  const overallConvRate = steps.length > 0 && maxCount > 0
    ? Math.round((steps[steps.length - 1].count / maxCount) * 100 * 10) / 10
    : 0;

  return (
    <div className="space-y-5" data-testid="analytics-funnel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif text-base font-medium">Conversion Funnel</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Track visitors from first visit to enquiry submission
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold font-serif text-emerald-700">{overallConvRate}%</p>
          <p className="text-xs text-muted-foreground">overall conversion</p>
        </div>
      </div>

      {/* Funnel steps */}
      {steps.length > 0 ? (
        <div className="space-y-0.5">
          {steps.map((step, i) => (
            <FunnelStep
              key={i}
              step={step}
              config={STEP_CONFIG[i] || STEP_CONFIG[STEP_CONFIG.length - 1]}
              maxCount={maxCount}
              isLast={i === steps.length - 1}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-border rounded-lg p-12 text-center">
          <p className="text-sm text-muted-foreground">No funnel data yet — visits will populate this as they occur</p>
        </div>
      )}

      {/* Summary stats */}
      {steps.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
          {steps.map((step, i) => (
            <div key={i} className="bg-white border border-border rounded-lg p-3 text-center">
              <p className="text-lg font-bold font-serif">{step.count.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{step.step}</p>
              {i > 0 && (
                <p className={`text-xs font-medium mt-1 ${step.pct >= 50 ? "text-emerald-600" : step.pct >= 20 ? "text-amber-600" : "text-rose-600"}`}>
                  {step.pct}% conversion
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Insight note */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-xs text-blue-800">
        <strong>How to read this:</strong> Each step shows how many of the previous step's visitors progressed further.
        A high drop-off between "Property View" and "Engaged" suggests property listings may need better photos, descriptions, or CTAs.
      </div>
    </div>
  );
}
